import React, { useState, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiLoader, FiUpload, FiX } from 'react-icons/fi';
import { API } from '../../services/api';
import { useToast } from '../../ToastContext';

import vixWaving from '../../assets/Vix/vix-waving.webp';
import vixTiltedHead from '../../assets/Vix/vix-tilted-head.webp';
import vixPointingUp from '../../assets/Vix/vix-pointing-up.webp';
import vixStandingFront from '../../assets/Vix/vix-standing-front.webp';
import vixPeekingFront from '../../assets/Vix/vix-peeking-front.webp';
import vixShy from '../../assets/Vix/vix-shy.webp';

export const ONBOARDING_STORAGE_KEY = 'vissory_onboarding_v1';

// ─── Step definitions per role ────────────────────────────────────────────────

const STEPS_CREATIVE = [
  {
    id: 'welcome',
    vixImg: vixWaving,
    vixAlt: 'Vix saludando',
    title: '¡Bienvenido/a a Vissory!',
    subtitle: 'Soy Vix, tu guía. En un par de minutos prepararemos tu perfil para que empieces a conectar con marcas y proyectos.',
    cta: 'Vamos allá',
  },
  {
    id: 'bio',
    vixImg: vixTiltedHead,
    vixAlt: 'Vix pensativo',
    title: 'Cuéntanos sobre ti',
    subtitle: 'Una buena biografía da confianza y ayuda a los clientes a entender qué puedes hacer por ellos.',
    cta: 'Continuar',
    field: 'bio',
  },
  {
    id: 'specialties',
    vixImg: vixPointingUp,
    vixAlt: 'Vix señalando',
    title: '¿En qué áreas destacas?',
    subtitle: 'Elige al menos una especialidad para aparecer en los filtros de búsqueda.',
    cta: 'Continuar',
    field: 'specialties',
  },
  {
    id: 'done',
    vixImg: vixStandingFront,
    vixAlt: 'Vix listo',
    title: '¡Perfil listo!',
    subtitle: 'Ya tienes lo esencial. Puedes completar el resto desde tu perfil cuando quieras.',
    cta: 'Empezar a usar Vissory',
  },
];

const STEPS_CREATOR = [
  {
    id: 'welcome',
    vixImg: vixWaving,
    vixAlt: 'Vix saludando',
    title: '¡Bienvenido/a a Vissory!',
    subtitle: 'Soy Vix, tu guía. Aquí podrás encontrar creativos para tus proyectos y también ser descubierto por marcas. Vamos a preparar tu perfil.',
    cta: 'Vamos allá',
  },
  {
    id: 'bio',
    vixImg: vixTiltedHead,
    vixAlt: 'Vix pensativo',
    title: 'Preséntate',
    subtitle: 'Cuéntanos quién eres. Tanto los creativos con los que colabores como las marcas lo verán antes de contactarte.',
    cta: 'Continuar',
    field: 'bio',
  },
  {
    id: 'content',
    vixImg: vixPointingUp,
    vixAlt: 'Vix señalando',
    title: '¿Qué contenido creas?',
    subtitle: 'Define tu tipo de contenido y tu nicho para aparecer en búsquedas relevantes.',
    cta: 'Continuar',
    field: 'content',
  },
  {
    id: 'done',
    vixImg: vixStandingFront,
    vixAlt: 'Vix listo',
    title: '¡Perfil listo!',
    subtitle: 'Ya tienes lo esencial. Puedes completar el resto desde tu perfil cuando quieras.',
    cta: 'Empezar a usar Vissory',
  },
];

const STEPS_BRAND = [
  {
    id: 'welcome',
    vixImg: vixWaving,
    vixAlt: 'Vix saludando',
    title: '¡Bienvenido/a a Vissory!',
    subtitle: 'Soy Vix, tu guía. Vamos a preparar el perfil de tu empresa para que los creativos sepan quiénes sois.',
    cta: 'Vamos allá',
  },
  {
    id: 'bio',
    vixImg: vixTiltedHead,
    vixAlt: 'Vix pensativo',
    title: 'Describe tu empresa',
    subtitle: 'Una buena descripción ayuda a los creativos a entender tu marca y a preparar mejores propuestas.',
    cta: 'Continuar',
    field: 'bio',
  },
  {
    id: 'company',
    vixImg: vixPointingUp,
    vixAlt: 'Vix señalando',
    title: 'Datos de la empresa',
    subtitle: 'Esta información aparecerá en tu perfil público y ayuda a los creativos a contextualizar sus propuestas.',
    cta: 'Continuar',
    field: 'company',
  },
  {
    id: 'featured',
    vixImg: vixShy,
    vixAlt: 'Vix tímido',
    title: 'Proyecto destacado',
    subtitle: 'Si eres una agencia, puedes destacar uno de tus proyectos en tu perfil público. Es opcional — puedes hacerlo después.',
    cta: 'Continuar',
    field: 'featured',
    optional: true,
  },
  {
    id: 'done',
    vixImg: vixStandingFront,
    vixAlt: 'Vix listo',
    title: '¡Perfil listo!',
    subtitle: 'Ya tienes lo esencial. Puedes completar el resto desde tu perfil cuando quieras.',
    cta: 'Empezar a usar Vissory',
  },
];

// ─── Specialty options (creative) ─────────────────────────────────────────────

const SPECIALTY_OPTIONS = [
  { group: 'Diseño', items: ['Diseño gráfico', 'Diseño de marca/branding', 'Diseño UX/UI', 'Ilustración', 'Dirección de arte'] },
  { group: 'Vídeo', items: ['Edición de vídeo', 'Motion graphics', 'Videografía', 'Vídeo para redes sociales'] },
  { group: 'Fotografía', items: ['Fotografía de producto', 'Fotografía de retrato', 'Fotografía de moda'] },
  { group: 'Copywriting', items: ['Copywriting publicitario', 'Redacción de contenidos', 'SEO copywriting'] },
  { group: 'Redes sociales', items: ['Community management', 'Gestión de redes sociales', 'Paid media/publicidad digital'] },
  { group: 'Estrategia', items: ['Estrategia de contenidos', 'Estrategia de marca', 'Consultoría de marketing'] },
  { group: 'Desarrollo web', items: ['Diseño web', 'Desarrollo frontend', 'WordPress/CMS'] },
  { group: 'Otros', items: ['Animación 2D', 'Animación 3D', 'Email marketing', 'Podcasting'] },
];

// ─── Step field components ─────────────────────────────────────────────────────

const BioField = ({ value, onChange, error, placeholder }) => (
  <div className="vix-tour-field">
    <textarea
      className={`vix-tour-textarea${error ? ' vix-tour-textarea--error' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={4}
      maxLength={500}
      autoFocus
    />
    <div className="vix-tour-field-meta">
      {error ? <span className="vix-tour-field-error">{error}</span> : <span />}
      <span className="vix-tour-char-count">{value.length}/500</span>
    </div>
  </div>
);

const SpecialtiesField = ({ value, onChange, error }) => (
  <div className="vix-tour-field">
    {error && <p className="vix-tour-field-error vix-tour-field-error--top">{error}</p>}
    <div className="vix-tour-specialties-scroll">
      {SPECIALTY_OPTIONS.map(group => (
        <div key={group.group} className="vix-tour-spec-group">
          <p className="vix-tour-spec-group-label">{group.group}</p>
          <div className="vix-tour-spec-chips">
            {group.items.map(item => {
              const selected = value.includes(item);
              return (
                <button
                  key={item}
                  type="button"
                  className={`vix-tour-spec-chip${selected ? ' vix-tour-spec-chip--selected' : ''}`}
                  onClick={() => onChange(selected ? value.filter(s => s !== item) : [...value, item])}
                >
                  {item}
                  {selected && <FiCheck className="vix-tour-spec-chip-icon" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
    {value.length > 0 && (
      <p className="vix-tour-spec-count">
        {value.length} especialidad{value.length !== 1 ? 'es' : ''} seleccionada{value.length !== 1 ? 's' : ''}
      </p>
    )}
  </div>
);

const ContentField = ({ contentFocus, contentNiche, onChange, error }) => (
  <div className="vix-tour-field" style={{ gap: '1rem' }}>
    {error && <p className="vix-tour-field-error vix-tour-field-error--top">{error}</p>}
    <div>
      <label className="vix-tour-label">Tipo de contenido</label>
      <input
        className="vix-tour-input"
        type="text"
        placeholder="Ej: Reels, Stories, Vídeo largo, Fotografía..."
        value={contentFocus}
        onChange={e => onChange('content_focus', e.target.value)}
        maxLength={100}
        autoFocus
      />
    </div>
    <div>
      <label className="vix-tour-label">Nicho o temática</label>
      <input
        className="vix-tour-input"
        type="text"
        placeholder="Ej: Lifestyle, Tecnología, Gastronomía, Moda..."
        value={contentNiche}
        onChange={e => onChange('content_niche', e.target.value)}
        maxLength={100}
      />
    </div>
  </div>
);

const CompanyField = ({ companyType, industry, companySize, onChange, error }) => (
  <div className="vix-tour-field" style={{ gap: '1rem' }}>
    {error && <p className="vix-tour-field-error vix-tour-field-error--top">{error}</p>}
    <div>
      <label className="vix-tour-label">Tipo de empresa</label>
      <select
        className="vix-tour-select"
        value={companyType}
        onChange={e => onChange('company_type', e.target.value)}
      >
        <option value="">— Elegir —</option>
        <option value="marca">Marca</option>
        <option value="agencia">Agencia</option>
        <option value="startup">Startup</option>
        <option value="corporacion">Corporación</option>
        <option value="otro">Otro</option>
      </select>
    </div>
    <div>
      <label className="vix-tour-label">Sector / industria</label>
      <input
        className="vix-tour-input"
        type="text"
        placeholder="Ej: Tecnología, Moda, Alimentación..."
        value={industry}
        onChange={e => onChange('industry', e.target.value)}
        maxLength={100}
      />
    </div>
    <div>
      <label className="vix-tour-label">Tamaño de la empresa</label>
      <select
        className="vix-tour-select"
        value={companySize}
        onChange={e => onChange('company_size', e.target.value)}
      >
        <option value="">— Elegir —</option>
        <option value="1-10">1–10 empleados</option>
        <option value="11-50">11–50 empleados</option>
        <option value="51-200">51–200 empleados</option>
        <option value="200+">200+ empleados</option>
      </select>
    </div>
  </div>
);

const FeaturedProjectField = ({ title, imageFile, imagePreview, onTitleChange, onFileChange, onRemoveFile }) => {
  const fileRef = useRef(null);
  return (
    <div className="vix-tour-field" style={{ gap: '1rem' }}>
      <div>
        <label className="vix-tour-label">Título del proyecto</label>
        <input
          className="vix-tour-input"
          type="text"
          placeholder="Ej: Campaña de verano para MarcaX"
          value={title}
          onChange={e => onTitleChange(e.target.value)}
          maxLength={80}
        />
      </div>
      <div>
        <label className="vix-tour-label">Imagen del proyecto</label>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="vix-tour-file-input"
          onChange={e => { const f = e.target.files?.[0]; if (f) onFileChange(f); e.target.value = ''; }}
        />
        {imagePreview ? (
          <div className="vix-tour-featured-preview">
            <img src={imagePreview} alt="Preview" className="vix-tour-featured-img" />
            <button type="button" className="vix-tour-featured-remove" onClick={onRemoveFile} aria-label="Quitar imagen">
              <FiX />
            </button>
          </div>
        ) : (
          <button type="button" className="vix-tour-upload-btn" onClick={() => fileRef.current?.click()}>
            <FiUpload /> Subir imagen
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Summary rows ──────────────────────────────────────────────────────────────

const SummaryRow = ({ label }) => (
  <div className="vix-tour-summary-row">
    <FiCheck className="vix-tour-summary-icon" />
    <span>{label}</span>
  </div>
);

const DoneSummary = ({ role, bio, specialties, contentFocus, companyType, industry, featuredTitle }) => {
  if (role === 'creative') return (
    <div className="vix-tour-summary">
      <SummaryRow label="Biografía añadida" />
      <SummaryRow label={`${specialties.length} especialidad${specialties.length !== 1 ? 'es' : ''} seleccionada${specialties.length !== 1 ? 's' : ''}`} />
    </div>
  );
  if (role === 'creator') return (
    <div className="vix-tour-summary">
      <SummaryRow label="Biografía añadida" />
      {contentFocus && <SummaryRow label={`Tipo de contenido: ${contentFocus}`} />}
    </div>
  );
  return (
    <div className="vix-tour-summary">
      <SummaryRow label="Descripción de empresa añadida" />
      {companyType && <SummaryRow label={`Tipo: ${companyType}`} />}
      {industry && <SummaryRow label={`Sector: ${industry}`} />}
      {featuredTitle && <SummaryRow label={`Proyecto destacado: ${featuredTitle}`} />}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

/**
 * VixOnboardingTour
 *
 * Multi-step guided modal. Steps vary by user role:
 *   creative → bio + specialties
 *   creator  → bio + content type/niche
 *   brand    → bio + company info + optional featured project
 *
 * Props:
 *   user        – logged-in user object (must include .role)
 *   token       – JWT auth token
 *   updateUser  – callback to update user state in the parent
 *   onComplete  – called when the tour finishes successfully
 *   onSkip      – called when the user skips the tour
 */
const VixOnboardingTour = ({ user, token, updateUser, onComplete, onSkip }) => {
  const { addToast } = useToast();
  const role = user?.role || 'creative';

  const steps = role === 'creator' ? STEPS_CREATOR : role === 'brand' ? STEPS_BRAND : STEPS_CREATIVE;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Fields — creative
  const [bio, setBio] = useState(user?.bio || '');
  const [specialties, setSpecialties] = useState(user?.specialties || []);
  const [bioError, setBioError] = useState('');
  const [specialtyError, setSpecialtyError] = useState('');

  // Fields — creator
  const [contentFocus, setContentFocus] = useState(user?.content_focus || '');
  const [contentNiche, setContentNiche] = useState(user?.content_niche || '');
  const [contentError, setContentError] = useState('');

  // Fields — brand
  const [companyType, setCompanyType] = useState(user?.company_type || '');
  const [industry, setIndustry] = useState(user?.industry || '');
  const [companySize, setCompanySize] = useState(user?.company_size || '');
  const [companyError, setCompanyError] = useState('');
  const [featuredTitle, setFeaturedTitle] = useState(user?.featured_project_title || '');
  const [featuredFile, setFeaturedFile] = useState(null);
  const [featuredPreview, setFeaturedPreview] = useState(user?.featured_project_image ? user.featured_project_image : null);

  const handleFeaturedFile = (file) => {
    setFeaturedFile(file);
    setFeaturedPreview(URL.createObjectURL(file));
  };
  const handleRemoveFeatured = () => { setFeaturedFile(null); setFeaturedPreview(null); };

  const handleContentChange = (field, value) => {
    if (field === 'content_focus') setContentFocus(value);
    else setContentNiche(value);
    if (contentError) setContentError('');
  };

  const handleCompanyChange = (field, value) => {
    if (field === 'company_type') setCompanyType(value);
    else if (field === 'industry') setIndustry(value);
    else setCompanySize(value);
    if (companyError) setCompanyError('');
  };

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;
  const progressPct = (step / (steps.length - 1)) * 100;

  const validate = () => {
    if (currentStep.field === 'bio') {
      if (!bio.trim()) { setBioError('Por favor escribe algo sobre ti.'); return false; }
      if (bio.trim().length < 20) { setBioError('Escribe al menos 20 caracteres.'); return false; }
      setBioError('');
    }
    if (currentStep.field === 'specialties') {
      if (specialties.length === 0) { setSpecialtyError('Selecciona al menos una especialidad.'); return false; }
      setSpecialtyError('');
    }
    if (currentStep.field === 'content') {
      if (!contentFocus.trim()) { setContentError('Indica al menos el tipo de contenido que creas.'); return false; }
      setContentError('');
    }
    if (currentStep.field === 'company') {
      if (!companyType && !industry) { setCompanyError('Rellena al menos el tipo de empresa o el sector.'); return false; }
      setCompanyError('');
    }
    return true;
  };

  const handleNext = async () => {
    if (!validate()) return;

    if (isLastStep) {
      await handleSave();
      return;
    }
    setStep(s => s + 1);
  };

  const handleSkipStep = () => setStep(s => s + 1);

  const handleSave = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      form.append('name', user.name || '');
      form.append('bio', bio.trim());

      if (role === 'creative') {
        specialties.filter(s => s.trim()).forEach(s => form.append('specialties', s));
      }
      if (role === 'creator') {
        if (contentFocus.trim()) form.append('content_focus', contentFocus.trim());
        if (contentNiche.trim()) form.append('content_niche', contentNiche.trim());
      }
      if (role === 'brand') {
        if (companyType) form.append('company_type', companyType);
        if (industry.trim()) form.append('industry', industry.trim());
        if (companySize) form.append('company_size', companySize);
        if (featuredTitle.trim()) form.append('featured_project_title', featuredTitle.trim());
        if (featuredFile) form.append('featured_project_image_file', featuredFile);
      }

      const response = await axios.put(`${API}/auth/profile`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      updateUser(response.data);
      try { localStorage.setItem(`${ONBOARDING_STORAGE_KEY}_${user.id}`, '1'); } catch (_) {}
      addToast({ type: 'success', title: '¡Perfil guardado!', description: 'Ya estás listo para empezar.' });
      onComplete();
    } catch (_) {
      addToast({ type: 'error', title: 'Error', description: 'No se pudo guardar el perfil. Inténtalo de nuevo.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vix-tour-overlay" role="dialog" aria-modal="true" aria-labelledby="vix-tour-title">
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          className="vix-tour-modal"
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="vix-tour-progress">
            <div className="vix-tour-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          {step === 0 && (
            <button type="button" className="vix-tour-skip" onClick={onSkip} aria-label="Saltar introducción">
              Saltar
            </button>
          )}

          <div className="vix-tour-mascot">
            <img src={currentStep.vixImg} alt={currentStep.vixAlt} className="vix-tour-mascot-img" />
          </div>

          <div className="vix-tour-body">
            <h2 id="vix-tour-title" className="vix-tour-title">{currentStep.title}</h2>
            <p className="vix-tour-subtitle">{currentStep.subtitle}</p>

            {currentStep.field === 'bio' && (
              <BioField
                value={bio}
                onChange={v => { setBio(v); if (bioError) setBioError(''); }}
                error={bioError}
                placeholder={
                  role === 'brand'
                    ? 'Ej: Agencia creativa especializada en branding y campañas digitales para marcas de consumo...'
                    : role === 'creator'
                    ? 'Ej: Creador de contenido lifestyle con +50k seguidores, especializado en reels y fotografía de producto...'
                    : 'Ej: Diseñadora gráfica con 5 años de experiencia en branding y packaging para marcas de consumo...'
                }
              />
            )}

            {currentStep.field === 'specialties' && (
              <SpecialtiesField value={specialties} onChange={setSpecialties} error={specialtyError} />
            )}

            {currentStep.field === 'content' && (
              <ContentField
                contentFocus={contentFocus}
                contentNiche={contentNiche}
                onChange={handleContentChange}
                error={contentError}
              />
            )}

            {currentStep.field === 'company' && (
              <CompanyField
                companyType={companyType}
                industry={industry}
                companySize={companySize}
                onChange={handleCompanyChange}
                error={companyError}
              />
            )}

            {currentStep.field === 'featured' && (
              <FeaturedProjectField
                title={featuredTitle}
                imageFile={featuredFile}
                imagePreview={featuredPreview}
                onTitleChange={setFeaturedTitle}
                onFileChange={handleFeaturedFile}
                onRemoveFile={handleRemoveFeatured}
              />
            )}

            {currentStep.id === 'done' && (
              <DoneSummary
                role={role}
                bio={bio}
                specialties={specialties}
                contentFocus={contentFocus}
                companyType={companyType}
                industry={industry}
                featuredTitle={featuredTitle}
              />
            )}
          </div>

          <div className="vix-tour-actions">
            {step > 0 && step < steps.length - 1 && (
              <button type="button" className="vix-tour-back" onClick={() => setStep(s => s - 1)}>
                ← Atrás
              </button>
            )}
            {currentStep.optional && (
              <button type="button" className="vix-tour-back" onClick={handleSkipStep}>
                Omitir
              </button>
            )}
            <button
              type="button"
              className="vix-tour-cta btn-primary"
              onClick={handleNext}
              disabled={saving}
            >
              {saving ? <FiLoader className="vix-tour-spinner" /> : currentStep.cta}
            </button>
          </div>

          <div className="vix-tour-dots" aria-hidden="true">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`vix-tour-dot${i === step ? ' vix-tour-dot--active' : i < step ? ' vix-tour-dot--done' : ''}`}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default VixOnboardingTour;
